import { useMemo } from 'react'
import {
  useCreatePOLineItemMatch,
  useDeletePOLineItemMatch,
  useDeletePOLineItemMatches,
  useGetPOLineItemMatchReasons,
  useGetPOLineItemMatches,
} from '@/service-library/hooks/po-matches'
import { useGetPDPurchaseOrders } from '@/services/hooks/relay/dynamic'
import { useGetVendorsLiveList } from '@/services/hooks/relay/live-lists'
import { PDPurchaseOrder } from '@/types/po-match'
import { PURCHASE_ORDER_INFO_TYPE_ID } from '@/utils/info-types'
import { showErrorSnackbar } from '@/utils/snackbars'

export const USER_MATCHED_REASON_ID = 'c566630e-0f32-4152-94f1-d92ca266e378'

type UsePOMatchDataOptions = {
  documentId: string
  ownerOrgId: string
  orgCredentialTypeId: string
  userAddedPOs: Map<string, PDPurchaseOrder>
}

export default function usePOMatchData({
  documentId,
  ownerOrgId,
  orgCredentialTypeId,
  userAddedPOs,
}: UsePOMatchDataOptions) {
  const { poLineItemMatchReasons } = useGetPOLineItemMatchReasons()

  const reasonMap = useMemo(
    () => new Map(poLineItemMatchReasons.map((r) => [r.id, r.name])),
    [poLineItemMatchReasons],
  )

  // Fetch line item matches directly by document ID
  const {
    poLineItemMatches,
    queryKey: poLineItemMatchesQueryKey,
    isPending: isPendingLineItemMatches,
  } = useGetPOLineItemMatches({
    filters: {
      limit: '1000',
      document_row__document_id: documentId,
    },
    enabled: !!documentId,
  })

  const { deletePOLineItemMatch } = useDeletePOLineItemMatch({
    listQueryKey: poLineItemMatchesQueryKey,
    onError: () => {
      showErrorSnackbar('Failed to delete line item match. Please try again.')
    },
  })

  const { deletePOLineItemMatches } = useDeletePOLineItemMatches({
    listQueryKey: poLineItemMatchesQueryKey,
    onError: () => {
      showErrorSnackbar('Failed to remove PO match. Please try again.')
    },
  })

  // Derive matched PO IDs from line item matches
  const matchedPoIds = useMemo(
    () => new Set(poLineItemMatches.map((lim) => lim.po_id)),
    [poLineItemMatches],
  )

  // Group line item matches by po_id
  const poLineItemMatchPerPoId = useMemo(
    () => Map.groupBy(poLineItemMatches, (lim) => lim.po_id),
    [poLineItemMatches],
  )

  // Combines saved PO matches and user-added, in-memory-only PO matches.
  // Sorted so the same set of IDs always produces the same order, keeping the
  // `ids` filter string (and its query key) stable across renders. This order
  // is not meaningful for display — usePODisplayOrder owns that.
  const userAddedPoIds = useMemo(
    () => new Set(userAddedPOs.keys()),
    [userAddedPOs],
  )
  const poIds = useMemo(
    () => [...matchedPoIds.union(userAddedPoIds)].sort(),
    [matchedPoIds, userAddedPoIds],
  )

  // Fetch POs
  const {
    pdPurchaseOrders: fetchedPurchaseOrders,
    isPending: isLoadingPurchaseOrders,
  } = useGetPDPurchaseOrders({
    infoTypeId: PURCHASE_ORDER_INFO_TYPE_ID,
    orgId: ownerOrgId,
    orgCredentialTypeId,
    filters: {
      ids: poIds.join(','),
    },
    enabled: poIds.length > 0,
    placeholderData: (previousData) => {
      if (!previousData || poIds.length === 0) return undefined
      const currentIds = new Set(poIds)
      const previousIds = new Set(
        previousData.pages.flatMap((page) =>
          page.results.map((po) => po.external_id),
        ),
      )
      // Use previous data when adding POs (all previous results still relevant)
      // or when removing POs (all current IDs covered by previous data).
      const allPreviousRelevant = previousIds.isSubsetOf(currentIds)
      const allCurrentCovered = currentIds.isSubsetOf(previousIds)
      return allPreviousRelevant || allCurrentCovered ? previousData : undefined
    },
  })

  const isLoadingPOs =
    isPendingLineItemMatches || (poIds.length > 0 && isLoadingPurchaseOrders)

  // Merge fetched POs with user-added POs (user-added take priority for
  // instant display). Driving the list off poIds drops stale entries, which
  // can linger in placeholder data after a PO is removed.
  const purchaseOrders = useMemo(() => {
    const merged = new Map(
      fetchedPurchaseOrders.map((po) => [po.external_id, po]),
    )
    userAddedPOs.forEach((po, id) => {
      if (!merged.has(id)) {
        merged.set(id, po)
      }
    })
    return poIds
      .map((id) => merged.get(id))
      .filter((po): po is PDPurchaseOrder => !!po)
  }, [fetchedPurchaseOrders, userAddedPOs, poIds])

  const { createPOLineItemMatch, isPending: isCreatingLineItemMatch } =
    useCreatePOLineItemMatch({
      listQueryKey: poLineItemMatchesQueryKey,
      onError: () => {
        showErrorSnackbar('Failed to create line item match. Please try again.')
      },
    })

  // Fetch all vendors
  const vendorIds = useMemo(
    () => purchaseOrders.map((po) => po.vendor_id).filter((id) => id),
    [purchaseOrders],
  )

  const { vendors } = useGetVendorsLiveList({
    orgId: ownerOrgId,
    orgCredentialTypeId,
    filters: {
      ids: vendorIds.join(','),
      limit: vendorIds.length ? vendorIds.length.toString() : undefined,
    },
    enabled: vendorIds.length > 0,
    placeholderData: (previousData) => previousData,
  })

  const vendorMap = useMemo(
    () => new Map(vendors.map((v) => [v.entity_id, v])),
    [vendors],
  )

  return {
    poIds,
    purchaseOrders,
    vendorMap,
    reasonMap,
    poLineItemMatches,
    isLoadingPOs,
    isCreatingLineItemMatch,
    poLineItemMatchPerPoId,
    poLineItemMatchesQueryKey,
    deletePOLineItemMatch,
    deletePOLineItemMatches,
    createPOLineItemMatch,
  }
}
